import React, { useEffect, useState, useCallback } from 'react';
import { styled } from '@mui/material/styles';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import {
    Box, Typography, Button, Table, TableHead, TableRow, TableBody, Paper, TableContainer,
    Chip, TextField, MenuItem, Select, FormControl, InputLabel, Stack, Dialog, DialogTitle,
    DialogContent, DialogActions, InputAdornment, Pagination, Grid, Divider, IconButton, Tooltip
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import { useDispatch, useSelector } from 'react-redux';
import {
    getInvoicesRequest, resetGetInvoices,
    addPaymentRequest, addPaymentSuccess, resetAddPayment,
    cancelInvoiceRequest, resetCancelInvoice,
    getInvoiceByIdRequest, resetGetInvoiceById,
    applyVoucherRequest, resetApplyVoucher,
    removeVoucherRequest, resetRemoveVoucher,
    setVoucherSerialRequest, resetSetVoucherSerial,
} from '../../../redux/modules/invoice/slice';
import { setMainHeading } from '../../../redux/modules/mainHeading/slice';
import { toast } from 'react-toastify';
import { USER_ROLE_DEFAULT } from '../../../constants/Constant';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.common.white,
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover,
    },
    '&:last-child td, &:last-child th': {
        border: 0,
    },
}));

const statusColors = {
    pending: 'warning',
    partial: 'info',
    paid: 'success',
    overdue: 'error',
    cancelled: 'default',
    refunded: 'secondary',
    draft: 'default',
};

const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '$0.00';
    return `$${Number(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// Render in the clinic's timezone when known so the date matches the deployment's
// local date, not each viewer's browser timezone (falls back to browser tz).
const formatDate = (date, timezone) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-GB', timezone ? { timeZone: timezone } : undefined);
};

const BillingInvoice = () => {
    const dispatch = useDispatch();
    const { userData } = useSelector(state => state.auth);
    const { getInvoicesRes, addPaymentRes, cancelInvoiceRes, getInvoiceByIdRes, applyVoucherRes, removeVoucherRes, setVoucherSerialRes } = useSelector(state => state.invoice);
    const { currentBranch } = useSelector(state => state.branch);
    const { localization } = useSelector(state => state.hospitalConfig);
    const tz = localization?.timezone;
    // Government health-voucher serial capture is gated to the Guyana voucher deployment.
    const voucherSubmission = useSelector(state => state.hospitalConfig?.features?.['lis.voucherSubmission'] === true);
    const isPatient = userData?.role === USER_ROLE_DEFAULT.PATIENT;

    const [statusFilter, setStatusFilter] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [page, setPage] = useState(1);
    const [paymentDialog, setPaymentDialog] = useState({ open: false, invoiceId: null });
    const [paymentData, setPaymentData] = useState({ amount: '', method: 'cash', reference: '', notes: '' });
    const [cancelDialog, setCancelDialog] = useState({ open: false, invoiceId: null });
    const [detailDialog, setDetailDialog] = useState({ open: false });
    const [voucherCode, setVoucherCode] = useState('');
    const [voucherSerial, setVoucherSerial] = useState('');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const limit = 20;

    const fetchInvoices = useCallback(() => {
        const params = { page, limit };
        if (statusFilter) params.status = statusFilter;
        if (searchQuery) params.searchQuery = searchQuery;
        dispatch(getInvoicesRequest(params));
    }, [dispatch, page, limit, statusFilter, searchQuery]);

    useEffect(() => {
        dispatch(setMainHeading("Billing & Invoicing"));
    }, [dispatch]);

    useEffect(() => {
        if (currentBranch?.id) fetchInvoices();
    }, [currentBranch?.id, fetchInvoices]);

    // Handle payment response
    useEffect(() => {
        if (addPaymentRes?.success) {
            toast.success(addPaymentRes.message);
            dispatch(resetAddPayment());
            setPaymentDialog({ open: false, invoiceId: null });
            setPaymentData({ amount: '', method: 'cash', reference: '', notes: '' });
            fetchInvoices();
        } else if (addPaymentRes && !addPaymentRes.success) {
            toast.error(addPaymentRes.message || 'Failed to record payment');
            dispatch(resetAddPayment());
        }
    }, [addPaymentRes]);

    // Handle cancel response
    useEffect(() => {
        if (cancelInvoiceRes?.success) {
            toast.success(cancelInvoiceRes.message);
            dispatch(resetCancelInvoice());
            setCancelDialog({ open: false, invoiceId: null });
            fetchInvoices();
        } else if (cancelInvoiceRes && !cancelInvoiceRes.success) {
            toast.error(cancelInvoiceRes.message || 'Failed to cancel invoice');
            dispatch(resetCancelInvoice());
        }
    }, [cancelInvoiceRes]);

    // Handle apply-voucher response
    useEffect(() => {
        if (applyVoucherRes?.success) {
            toast.success(applyVoucherRes.message);
            dispatch(resetApplyVoucher());
            setVoucherCode('');
            if (applyVoucherRes.data?._id) dispatch(getInvoiceByIdRequest(applyVoucherRes.data._id));
            fetchInvoices();
        } else if (applyVoucherRes && !applyVoucherRes.success) {
            toast.error(applyVoucherRes.message || 'Failed to apply voucher');
            dispatch(resetApplyVoucher());
        }
    }, [applyVoucherRes]); // eslint-disable-line react-hooks/exhaustive-deps

    // Handle remove-voucher response
    useEffect(() => {
        if (removeVoucherRes?.success) {
            toast.success(removeVoucherRes.message);
            dispatch(resetRemoveVoucher());
            if (removeVoucherRes.data?._id) dispatch(getInvoiceByIdRequest(removeVoucherRes.data._id));
            fetchInvoices();
        } else if (removeVoucherRes && !removeVoucherRes.success) {
            toast.error(removeVoucherRes.message || 'Failed to remove voucher');
            dispatch(resetRemoveVoucher());
        }
    }, [removeVoucherRes]); // eslint-disable-line react-hooks/exhaustive-deps

    // Keep the editable serial field in sync with whichever invoice is open.
    useEffect(() => {
        setVoucherSerial(getInvoiceByIdRes?.data?.voucherSerialNo || '');
    }, [getInvoiceByIdRes?.data?._id, getInvoiceByIdRes?.data?.voucherSerialNo]);

    // Handle set-voucher-serial response
    useEffect(() => {
        if (setVoucherSerialRes?.success) {
            toast.success(setVoucherSerialRes.message);
            dispatch(resetSetVoucherSerial());
            if (setVoucherSerialRes.data?._id) dispatch(getInvoiceByIdRequest(setVoucherSerialRes.data._id));
        } else if (setVoucherSerialRes && !setVoucherSerialRes.success) {
            toast.error(setVoucherSerialRes.message || 'Failed to save voucher serial');
            dispatch(resetSetVoucherSerial());
        }
    }, [setVoucherSerialRes]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleApplyVoucher = () => {
        if (!voucherCode.trim()) {
            toast.error('Enter a voucher code');
            return;
        }
        dispatch(applyVoucherRequest({ id: detailInvoice._id, code: voucherCode.trim().toUpperCase() }));
    };

    const handleRemoveVoucher = () => {
        dispatch(removeVoucherRequest(detailInvoice._id));
    };

    const handleSaveVoucherSerial = () => {
        dispatch(setVoucherSerialRequest({ id: detailInvoice._id, voucherSerialNo: voucherSerial.trim() }));
    };

    const handleRecordPayment = () => {
        if (!paymentData.amount || Number(paymentData.amount) <= 0) {
            toast.error('Please enter a valid payment amount');
            return;
        }
        dispatch(addPaymentRequest({
            id: paymentDialog.invoiceId,
            amount: Number(paymentData.amount),
            method: paymentData.method,
            reference: paymentData.reference,
            notes: paymentData.notes,
        }));
    };

    const handleCancelInvoice = () => {
        dispatch(cancelInvoiceRequest(cancelDialog.invoiceId));
    };

    const handleViewDetail = (invoiceId) => {
        dispatch(getInvoiceByIdRequest(invoiceId));
        setDetailDialog({ open: true });
    };

    const handlePrintInvoice = (invoiceId) => {
        const token = localStorage.getItem('item') || localStorage.getItem('token');
        const baseUrl = process.env.REACT_APP_SERVER_API || '';
        window.open(`${baseUrl}api/invoices/${invoiceId}/print?token=${encodeURIComponent(token)}`, '_blank');
    };

    // Export all invoices in the chosen date range to CSV (opens in Excel), with a
    // totals summary row. Uses fetch+blob so the branch header can be sent.
    const handleExport = async () => {
        try {
            const token = localStorage.getItem('item') || localStorage.getItem('token');
            const baseUrl = process.env.REACT_APP_SERVER_API || '';
            // Send the picked local days as exact UTC instants (local start/end of
            // day) so the range matches the viewer's timezone, not the server's —
            // otherwise today's invoices fall outside the window.
            const startISO = (d) => { const [y, m, day] = d.split('-').map(Number); return new Date(y, m - 1, day, 0, 0, 0, 0).toISOString(); };
            const endISO = (d) => { const [y, m, day] = d.split('-').map(Number); return new Date(y, m - 1, day, 23, 59, 59, 999).toISOString(); };
            const qs = new URLSearchParams();
            if (dateFrom) qs.set('dateFrom', startISO(dateFrom));
            if (dateTo) qs.set('dateTo', endISO(dateTo));
            const res = await fetch(`${baseUrl}api/invoices/export?${qs.toString()}`, {
                headers: { Authorization: token, 'X-Current-Branch': currentBranch?.id || '' },
            });
            if (!res.ok) { toast.error('Export failed'); return; }
            const blob = await res.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Invoices_${dateFrom || 'all'}_${dateTo || 'all'}.csv`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch {
            toast.error('Export failed');
        }
    };

    const invoices = getInvoicesRes?.data || [];
    const totalCount = getInvoicesRes?.dataCount || 0;
    const totalPages = Math.ceil(totalCount / limit);
    const detailInvoice = getInvoiceByIdRes?.data;

    return (
        <>
            {/* Filters */}
            <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <TextField
                    size="small"
                    placeholder="Search invoice #, patient name, MRN..."
                    value={searchQuery}
                    onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
                    sx={{ minWidth: 280 }}
                />
                <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>Status</InputLabel>
                    <Select
                        value={statusFilter}
                        label="Status"
                        onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                    >
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="pending">Pending</MenuItem>
                        <MenuItem value="partial">Partial</MenuItem>
                        <MenuItem value="paid">Paid</MenuItem>
                        <MenuItem value="overdue">Overdue</MenuItem>
                        <MenuItem value="cancelled">Cancelled</MenuItem>
                    </Select>
                </FormControl>
                {!isPatient && (
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', ml: { md: 'auto' } }}>
                        <TextField
                            size="small" type="date" label="From" InputLabelProps={{ shrink: true }}
                            value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                        />
                        <TextField
                            size="small" type="date" label="To" InputLabelProps={{ shrink: true }}
                            value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                        />
                        <Button variant="outlined" onClick={handleExport}>Export CSV</Button>
                    </Box>
                )}
            </Box>

            {/* Invoice Table */}
            <Paper className='cardStyle'>
                <TableContainer sx={{ overflowX: { xs: 'auto', md: 'visible' } }}>
                    <Table className='commonTable'>
                        <TableHead>
                            <TableRow>
                                <StyledTableCell>Invoice #</StyledTableCell>
                                {!isPatient && <StyledTableCell>Patient</StyledTableCell>}
                                <StyledTableCell>Order</StyledTableCell>
                                <StyledTableCell>Date</StyledTableCell>
                                <StyledTableCell align="right">Total</StyledTableCell>
                                <StyledTableCell align="right">Paid</StyledTableCell>
                                <StyledTableCell align="right">Due</StyledTableCell>
                                <StyledTableCell>Status</StyledTableCell>
                                <StyledTableCell>Actions</StyledTableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {invoices.length > 0 ? invoices.map((inv) => (
                                <StyledTableRow key={inv._id}>
                                    <StyledTableCell sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
                                        {inv.invoiceNumber}
                                    </StyledTableCell>
                                    {!isPatient && (
                                        <StyledTableCell>
                                            {inv.patientId ? `${inv.patientId.fName} ${inv.patientId.lName}` : '-'}
                                        </StyledTableCell>
                                    )}
                                    <StyledTableCell sx={{ fontFamily: 'monospace', fontSize: 13 }}>
                                        {inv.labOrderId?.labOrderId || '-'}
                                    </StyledTableCell>
                                    <StyledTableCell>{formatDate(inv.createdAt, tz)}</StyledTableCell>
                                    <StyledTableCell align="right">{formatCurrency(inv.totalAmount)}</StyledTableCell>
                                    <StyledTableCell align="right">{formatCurrency(inv.amountPaid)}</StyledTableCell>
                                    <StyledTableCell align="right" sx={{ fontWeight: inv.amountDue > 0 ? 600 : 400 }}>
                                        {formatCurrency(inv.amountDue)}
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        <Chip
                                            label={inv.status?.charAt(0).toUpperCase() + inv.status?.slice(1)}
                                            color={statusColors[inv.status] || 'default'}
                                            size="small"
                                        />
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        <Stack direction="row" spacing={1}>
                                            <Button size="small" variant="outlined" onClick={() => handleViewDetail(inv._id)}>
                                                View
                                            </Button>
                                            {!isPatient && inv.status !== 'paid' && inv.status !== 'cancelled' && inv.status !== 'refunded' && (
                                                <Button
                                                    size="small"
                                                    variant="contained"
                                                    color="primary"
                                                    onClick={() => setPaymentDialog({ open: true, invoiceId: inv._id })}
                                                >
                                                    Pay
                                                </Button>
                                            )}
                                            {!isPatient && inv.amountPaid === 0 && inv.status !== 'cancelled' && (
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    color="error"
                                                    onClick={() => setCancelDialog({ open: true, invoiceId: inv._id })}
                                                >
                                                    Cancel
                                                </Button>
                                            )}
                                        </Stack>
                                    </StyledTableCell>
                                </StyledTableRow>
                            )) : (
                                <StyledTableRow>
                                    <StyledTableCell colSpan={isPatient ? 8 : 9} align="center">
                                        <Typography color="text.secondary" sx={{ py: 3 }}>
                                            No invoices found
                                        </Typography>
                                    </StyledTableCell>
                                </StyledTableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                {totalPages > 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                        <Pagination count={totalPages} page={page} onChange={(e, v) => setPage(v)} />
                    </Box>
                )}
            </Paper>

            {/* Record Payment Dialog */}
            <Dialog open={paymentDialog.open} onClose={() => setPaymentDialog({ open: false, invoiceId: null })} maxWidth="sm" fullWidth>
                <DialogTitle>Record Payment</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField
                            label="Amount"
                            type="number"
                            fullWidth
                            value={paymentData.amount}
                            onChange={(e) => setPaymentData({ ...paymentData, amount: e.target.value })}
                            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                        />
                        <FormControl fullWidth>
                            <InputLabel>Payment Method</InputLabel>
                            <Select
                                value={paymentData.method}
                                label="Payment Method"
                                onChange={(e) => setPaymentData({ ...paymentData, method: e.target.value })}
                            >
                                <MenuItem value="cash">Cash</MenuItem>
                                <MenuItem value="card">Card</MenuItem>
                                <MenuItem value="insurance">Insurance</MenuItem>
                                <MenuItem value="online">Online</MenuItem>
                                <MenuItem value="other">Other</MenuItem>
                            </Select>
                        </FormControl>
                        <TextField
                            label="Reference (receipt #, txn ID, etc.)"
                            fullWidth
                            value={paymentData.reference}
                            onChange={(e) => setPaymentData({ ...paymentData, reference: e.target.value })}
                        />
                        <TextField
                            label="Notes"
                            fullWidth
                            multiline
                            rows={2}
                            value={paymentData.notes}
                            onChange={(e) => setPaymentData({ ...paymentData, notes: e.target.value })}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPaymentDialog({ open: false, invoiceId: null })}>Cancel</Button>
                    <Button variant="contained" onClick={handleRecordPayment}>Record Payment</Button>
                </DialogActions>
            </Dialog>

            {/* Cancel Confirmation Dialog */}
            <Dialog open={cancelDialog.open} onClose={() => setCancelDialog({ open: false, invoiceId: null })}>
                <DialogTitle>Cancel Invoice</DialogTitle>
                <DialogContent>
                    <Typography>Are you sure you want to cancel this invoice? This action cannot be undone.</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCancelDialog({ open: false, invoiceId: null })}>No</Button>
                    <Button variant="contained" color="error" onClick={handleCancelInvoice}>Yes, Cancel</Button>
                </DialogActions>
            </Dialog>

            {/* Invoice Detail Dialog */}
            <Dialog
                open={detailDialog.open}
                onClose={() => { setDetailDialog({ open: false }); dispatch(resetGetInvoiceById()); }}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6">Invoice {detailInvoice?.invoiceNumber}</Typography>
                        {detailInvoice?.status && (
                            <Chip
                                label={detailInvoice.status.charAt(0).toUpperCase() + detailInvoice.status.slice(1)}
                                color={statusColors[detailInvoice.status] || 'default'}
                            />
                        )}
                    </Stack>
                </DialogTitle>
                <DialogContent dividers>
                    {detailInvoice ? (
                        <Stack spacing={3}>
                            {/* Patient Info */}
                            <Grid container spacing={2}>
                                <Grid item xs={6} sm={3}>
                                    <Typography variant="caption" color="text.secondary">Patient</Typography>
                                    <Typography>{detailInvoice.patientId?.fName} {detailInvoice.patientId?.lName}</Typography>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <Typography variant="caption" color="text.secondary">MRN</Typography>
                                    <Typography sx={{ fontFamily: 'monospace' }}>{detailInvoice.patientId?.mrn || '-'}</Typography>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <Typography variant="caption" color="text.secondary">Order</Typography>
                                    <Typography sx={{ fontFamily: 'monospace' }}>{detailInvoice.labOrderId?.labOrderId || '-'}</Typography>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <Typography variant="caption" color="text.secondary">Date</Typography>
                                    <Typography>{formatDate(detailInvoice.createdAt, tz)}</Typography>
                                </Grid>
                            </Grid>

                            <Divider />

                            {/* Line Items */}
                            <Box>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>Line Items</Typography>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Description</TableCell>
                                            <TableCell align="right">Qty</TableCell>
                                            <TableCell align="right">Unit Price</TableCell>
                                            <TableCell align="right">Total</TableCell>
                                            <TableCell>Status</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {detailInvoice.lineItems?.map((li, i) => (
                                            <TableRow key={li._id || i} sx={li.isCancelled ? { opacity: 0.5, textDecoration: 'line-through' } : {}}>
                                                <TableCell>{li.description}</TableCell>
                                                <TableCell align="right">{li.quantity}</TableCell>
                                                <TableCell align="right">{formatCurrency(li.unitPrice)}</TableCell>
                                                <TableCell align="right">{formatCurrency(li.lineTotal)}</TableCell>
                                                <TableCell>{li.isCancelled ? <Chip label="Cancelled" size="small" color="default" /> : ''}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </Box>

                            {/* Totals */}
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <Stack spacing={0.5} sx={{ minWidth: 200 }}>
                                    <Stack direction="row" justifyContent="space-between">
                                        <Typography variant="body2">Subtotal:</Typography>
                                        <Typography variant="body2">{formatCurrency(detailInvoice.subtotal)}</Typography>
                                    </Stack>
                                    {detailInvoice.discountPercent > 0 && (
                                        <Stack direction="row" justifyContent="space-between">
                                            <Typography variant="body2">Discount ({detailInvoice.discountPercent}%):</Typography>
                                            <Typography variant="body2" color="error">-{formatCurrency(detailInvoice.discountAmount)}</Typography>
                                        </Stack>
                                    )}
                                    {detailInvoice.voucherDiscount > 0 && (
                                        <Stack direction="row" justifyContent="space-between">
                                            <Typography variant="body2">
                                                Voucher{detailInvoice.voucher?.code ? ` (${detailInvoice.voucher.code})` : ''}:
                                            </Typography>
                                            <Typography variant="body2" color="error">-{formatCurrency(detailInvoice.voucherDiscount)}</Typography>
                                        </Stack>
                                    )}
                                    {detailInvoice.taxPercent > 0 && (
                                        <Stack direction="row" justifyContent="space-between">
                                            <Typography variant="body2">Tax ({detailInvoice.taxPercent}%):</Typography>
                                            <Typography variant="body2">{formatCurrency(detailInvoice.taxAmount)}</Typography>
                                        </Stack>
                                    )}
                                    <Divider />
                                    <Stack direction="row" justifyContent="space-between">
                                        <Typography variant="subtitle2">Total:</Typography>
                                        <Typography variant="subtitle2">{formatCurrency(detailInvoice.totalAmount)}</Typography>
                                    </Stack>
                                    <Stack direction="row" justifyContent="space-between">
                                        <Typography variant="body2" color="success.main">Paid:</Typography>
                                        <Typography variant="body2" color="success.main">{formatCurrency(detailInvoice.amountPaid)}</Typography>
                                    </Stack>
                                    <Stack direction="row" justifyContent="space-between">
                                        <Typography variant="subtitle2" color={detailInvoice.amountDue > 0 ? 'error' : 'success.main'}>
                                            Balance Due:
                                        </Typography>
                                        <Typography variant="subtitle2" color={detailInvoice.amountDue > 0 ? 'error' : 'success.main'}>
                                            {formatCurrency(detailInvoice.amountDue)}
                                        </Typography>
                                    </Stack>
                                </Stack>
                            </Box>

                            {/* Voucher / Discount code */}
                            {!isPatient && !['cancelled', 'refunded', 'paid'].includes(detailInvoice.status) && (
                                <Box>
                                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Voucher / Discount Code</Typography>
                                    {detailInvoice.voucher?.code ? (
                                        <Stack direction="row" spacing={1.5} alignItems="center">
                                            <Chip label={detailInvoice.voucher.code} color="success" variant="outlined" />
                                            <Typography variant="body2" color="text.secondary">
                                                {detailInvoice.voucher.discountType === 'percentage'
                                                    ? `${detailInvoice.voucher.value}% off`
                                                    : `${formatCurrency(detailInvoice.voucher.value)} off`}
                                            </Typography>
                                            <Button size="small" color="error" onClick={handleRemoveVoucher}>Remove</Button>
                                        </Stack>
                                    ) : (
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <TextField
                                                size="small"
                                                placeholder="Enter code"
                                                value={voucherCode}
                                                onChange={(e) => setVoucherCode(e.target.value.toUpperCase())}
                                                inputProps={{ style: { textTransform: 'uppercase', fontFamily: 'monospace' } }}
                                                sx={{ maxWidth: 220 }}
                                                onKeyDown={(e) => { if (e.key === 'Enter') handleApplyVoucher(); }}
                                            />
                                            <Button size="small" variant="contained" onClick={handleApplyVoucher}>Apply</Button>
                                        </Stack>
                                    )}
                                </Box>
                            )}

                            {/* Government health-voucher serial (Guyana voucher submission) */}
                            {voucherSubmission && !isPatient && (
                                <Box>
                                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Govt. Voucher Serial No.</Typography>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <TextField
                                            size="small"
                                            placeholder="e.g. A-0284912"
                                            value={voucherSerial}
                                            onChange={(e) => setVoucherSerial(e.target.value)}
                                            sx={{ maxWidth: 220 }}
                                            onKeyDown={(e) => { if (e.key === 'Enter') handleSaveVoucherSerial(); }}
                                        />
                                        <Button size="small" variant="contained" onClick={handleSaveVoucherSerial}>Save</Button>
                                    </Stack>
                                    <Typography variant="caption" color="text.secondary">
                                        The serial on the government voucher the patient presented — pulled into the Voucher Submission export.
                                    </Typography>
                                </Box>
                            )}

                            {/* Payments */}
                            {detailInvoice.payments?.length > 0 && (
                                <Box>
                                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Payment History</Typography>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Date</TableCell>
                                                <TableCell>Method</TableCell>
                                                <TableCell align="right">Amount</TableCell>
                                                <TableCell>Reference</TableCell>
                                                <TableCell>Received By</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {detailInvoice.payments.map((p, i) => (
                                                <TableRow key={p._id || i}>
                                                    <TableCell>{formatDate(p.paidAt, tz)}</TableCell>
                                                    <TableCell sx={{ textTransform: 'capitalize' }}>{p.method}</TableCell>
                                                    <TableCell align="right">{formatCurrency(p.amount)}</TableCell>
                                                    <TableCell>{p.reference || '-'}</TableCell>
                                                    <TableCell>{p.receivedBy?.name || p.receivedBy?.firstName || '-'}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </Box>
                            )}

                            {/* Notes */}
                            {detailInvoice.notes && (
                                <Box>
                                    <Typography variant="subtitle2">Notes</Typography>
                                    <Typography variant="body2" color="text.secondary">{detailInvoice.notes}</Typography>
                                </Box>
                            )}
                        </Stack>
                    ) : (
                        <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>Loading...</Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    {detailInvoice && (
                        <Button startIcon={<PrintIcon />} onClick={() => handlePrintInvoice(detailInvoice._id)}>
                            Print
                        </Button>
                    )}
                    <Button onClick={() => { setDetailDialog({ open: false }); dispatch(resetGetInvoiceById()); }}>Close</Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default BillingInvoice;
